import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import Divider from '@mui/material/Divider'
import Form, { ButtonItem, SimpleItem, EmptyItem, ColCountByScreen, GroupItem, Label } from 'devextreme-react/form'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import Typography from '@mui/material/Typography'
import CustomStore from 'devextreme/data/custom_store'
import { useSnackbar } from 'notistack'
import utilServices from 'app/services/util-services'
import customerServices from 'app/services/pages/reports/registrations/CustomerServices/customerServices'
import DataSource from 'devextreme/data/data_source'
import { PatternRule } from 'devextreme-react/data-grid'
import { useSelector } from 'react-redux'
import { IconButton, Stack, useMediaQuery } from '@mui/material'
import WestIcon from '@mui/icons-material/West'
import GenderTypeService from 'app/utils/components/GenderType/GenderTypeService'
import { DropDownButtonRender } from 'app/utils/DropDownButtonRender/DropDownButtonRender'
import { SelectBox } from 'devextreme-react'

const UpdateTeacherGridForm = ({ teacher, updateTeacher }) => {
    const { enqueueSnackbar } = useSnackbar()
    const navigate = useNavigate()
    const { handleSubmit } = useForm()
    const [labelLocation, setLabelLocation] = useState('left')
    const [formData, setFormData] = useState({
        ...teacher,
        password: '',
        verifyPassword: '',
    })
    const { loading } = useSelector((state) => state.teachers)
    const sm = useMediaQuery('(max-width:600px)')
    const [customerLoading, setCustomerLoading] = useState(false)
    const isCustomerLocked = useMemo(
        () => !!teacher?.customerId && !teacher?.customerLinkStale,
        [teacher?.customerId, teacher?.customerLinkStale]
    )

    useEffect(() => {
        setLabelLocation(sm ? 'top' : 'left')
    }, [sm])

    useEffect(() => setFormData({ ...teacher, password: '', verifyPassword: '' }), [teacher])

    const onFieldDataChanged = useCallback((e) => {
        if (e.dataField) {
            setFormData((prev) => ({ ...prev, [e.dataField]: e.value }))
        }
    }, [])

    function handleError(error) {
        if (error.status === '401') {
            navigate('/profile/signout')
        } else if (error.detail) {
            enqueueSnackbar(error.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar(error, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    const submitButtonOptions = {
        text: 'Save',
        type: 'default',
        width: 120,
        useSubmitBehavior: true,
    }

    const cancelButtonOptions = {
        text: 'Cancel',
        type: 'Normal',
        width: 120,
        onClick: () => navigate('/security/teachers', { state: { navigateFrom: 'cancel-update-teacher' } }),
    }

    const validationRules = useMemo(
        () => ({
            firstName: [{ type: 'required', message: 'First Name is required.' }],
            lastName: [{ type: 'required', message: 'Last Name is required.' }],
            email: [{ type: 'required', message: 'Email is required.' }],
            password: formData.password ? [{ type: 'required', message: 'Password is required.' }] : [],
            verifyPassword: formData.verifyPassword
                ? [
                      { type: 'required', message: 'Verify Password is required.' },
                      {
                          type: 'custom',
                          validationCallback: (e) => (formData.password ? !!e.value : true),
                          message: 'Please enter password and verify password',
                      },
                      {
                          type: 'custom',
                          validationCallback: (e) => (formData.password ? e.value === formData.password : true),
                          message: 'Password and Verify Password do not match.',
                      },
                  ]
                : [],
        }),
        [formData.password, formData.verifyPassword]
    )

    const customerLookupList = useCallback(async (searchTerm) => {
        try {
            return await customerServices.getCustomerLookupList({ firstName: searchTerm })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const customerLookupStore = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async (loadOptions) => {
                        const searchTerm = loadOptions.searchValue || ''
                        return await customerLookupList(searchTerm)
                    },
                    byKey: async (key) => {
                        if (key === '') return utilServices.emptyList()

                        setCustomerLoading(true)

                        try {
                            return await customerServices.getCustomerLookupById(key)
                        } catch (error) {
                            handleError(error)
                        } finally {
                            setCustomerLoading(false)
                        }
                    },
                    errorHandler: (e) => console.error(e),
                }),
                reshapeOnPush: true,
            }),
        []
    )

    const onCustomerValueChanged = useCallback((e) => {
        if (isCustomerLocked) return
        setFormData((prev) => ({ ...prev, customerId: e.value }))
    }, [isCustomerLocked])

    const customerFullName = useMemo(() => {
        return (data) => {
            return data ? data.firstName.toFullName(data.lastName) : ''
        }
    }, [])

    const phoneEditorOptions = {
        valueChangeEvent: 'keyup',
        maskRules: {
            X: /\d/,
        },
    }

    const phonePattern = /^\+[1-9]\d{10,14}$/

    return (
        <React.Fragment>
            <JumboCardQuick
                title={
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <IconButton
                            className='backArrowForm'
                            onClick={() =>
                                navigate(`/security/teachers`, {
                                    state: { navigateFrom: 'cancel-update-teacher' },
                                })
                            }>
                            <WestIcon />
                        </IconButton>
                        <Typography color={'common.white'} variant={'h3'} sx={{ mt: 0.8 }}>
                            Update Teacher
                        </Typography>
                    </Stack>
                }
                headerSx={{
                    height: 40,
                    bgcolor: 'primary.main',
                }}>
                <form
                    onSubmit={handleSubmit(() => {
                        if (formData.password && !formData.verifyPassword) {
                            enqueueSnackbar('Verify Password must be entered if Password is provided.', {
                                variant: 'error',
                                anchorOrigin: {
                                    vertical: 'top',
                                    horizontal: 'right',
                                },
                            })
                            return
                        }
                        if (formData.password && formData.password !== formData.verifyPassword) {
                            enqueueSnackbar('Password and Verify Password do not match.', {
                                variant: 'error',
                                anchorOrigin: {
                                    vertical: 'top',
                                    horizontal: 'right',
                                },
                            })
                            return
                        }
                        updateTeacher(formData)
                    })}>
                    <Form
                        mode='form'
                        formData={formData}
                        onFieldDataChanged={onFieldDataChanged}
                        showColonAfterLabel={false}
                        showValidationSummary={false}
                        labelLocation={labelLocation}>
                        <GroupItem caption='Update Teacher' cssClass='form-group-item '>
                            <ColCountByScreen lg={13} md={13} sm={1} xs={1} />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem cssClass='form-group-item' colSpan={6}>
                                <EmptyItem />
                                <SimpleItem dataField='firstName' validationRules={validationRules.firstName}>
                                    <Label text='First Name' />
                                </SimpleItem>
                                <SimpleItem dataField='lastName' validationRules={validationRules.lastName}>
                                    <Label text='Last Name' />
                                </SimpleItem>
                                <SimpleItem dataField='phoneNumber' editorOptions={phoneEditorOptions}>
                                    <Label text='Phone Number (Optional)' />
                                    <PatternRule
                                        message='Phone number must start with + and needs to include country code'
                                        pattern={phonePattern}
                                    />
                                </SimpleItem>
                                <SimpleItem dataField='email' validationRules={validationRules.email}>
                                    <Label text='Email' />
                                </SimpleItem>
                                <SimpleItem
                                    dataField='password'
                                    editorType='dxTextBox'
                                    validationRules={validationRules.password}
                                    editorOptions={{
                                        mode: 'password',
                                        value: formData.password,
                                        onValueChanged: (e) => setFormData((prev) => ({ ...prev, password: e.value })),
                                        validationMessageMode: 'always',
                                    }}
                                />
                                <SimpleItem
                                    dataField='verifyPassword'
                                    editorType='dxTextBox'
                                    validationRules={validationRules.verifyPassword}
                                    editorOptions={{
                                        mode: 'password',
                                        value: formData.verifyPassword,
                                        onValueChanged: (e) =>
                                            setFormData((prev) => ({ ...prev, verifyPassword: e.value })),
                                        validationMessageMode: 'always',
                                    }}
                                />
                                <SimpleItem dataField='comments' editorType='dxTextArea'>
                                    <Label text='Comments' />
                                </SimpleItem>
                                <SimpleItem
                                    dataField='genderType'
                                    editorType='dxSelectBox'
                                    editorOptions={{
                                        dataSource: GenderTypeService.getGenderTypeForAccount(),
                                        displayExpr: 'genderType',
                                        valueExpr: 'value',
                                        searchEnabled: true,
                                        showClearButton: true,
                                    }}>
                                    <Label text='Gender Type' />
                                </SimpleItem>
                                <EmptyItem />
                                <GroupItem>
                                    <Divider />
                                </GroupItem>
                                <EmptyItem />
                                <SimpleItem>
                                    <Label text='Customer Name' />
                                    <SelectBox
                                        dataSource={customerLookupStore}
                                        searchEnabled={true}
                                        showClearButton={!isCustomerLocked}
                                        defaultValue={teacher.customerId}
                                        value={formData?.customerId}
                                        valueExpr='id'
                                        displayExpr={customerFullName}
                                        itemRender={customerFullName}
                                        onValueChanged={onCustomerValueChanged}
                                        readOnly={isCustomerLocked}
                                        dropDownButtonRender={DropDownButtonRender(customerLoading)}></SelectBox>
                                </SimpleItem>
                            </GroupItem>
                        </GroupItem>
                        <EmptyItem />
                        <GroupItem>
                            <ColCountByScreen lg={2} md={2} sm={2} xs={2} />
                            <ButtonItem
                                buttonOptions={{
                                    disabled: loading,
                                    icon: loading ? 'refresh' : 'check',
                                    ...submitButtonOptions,
                                }}
                                horizontalAlignment='right'
                            />
                            <ButtonItem buttonOptions={cancelButtonOptions} horizontalAlignment='left' />
                        </GroupItem>
                    </Form>
                </form>
            </JumboCardQuick>
        </React.Fragment>
    )
}

export default UpdateTeacherGridForm
